import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import logo from "@/assets/centralflow-owl-logo.svg";
import { Menu, X, ChevronDown, Hotel, Home, Tent, UtensilsCrossed, Wine, Dumbbell, Sparkles, Flag } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
// DropdownMenu import removed - using NavigationMenu for consistency
import { useTranslation } from "react-i18next";

// NO STATIC IMAGE IMPORTS - Images loaded on dropdown interaction only

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownImages, setDropdownImages] = useState<Record<string, string>>({});
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();

  // Helper to check if current path matches
  const isActive = (path: string) => location.pathname === path;
  const isResourcesActive = () => ['/faq', '/guides', '/blog'].includes(location.pathname);
  const isSolutionsActive = () => location.pathname.startsWith('/solutions');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lazy load dropdown images only on first hover/interaction
  const loadDropdownImages = useCallback(async () => {
    if (imagesLoaded) return;
    
    const [
      bgHotel,
      bgRental,
      bgCampsite,
      bgRestaurant,
      bgBar,
      bgFitness,
      bgSpa,
      bgGolf,
    ] = await Promise.all([
      import("@/assets/solutions-hotel.webp"),
      import("@/assets/solutions-serviced-apartments.webp"),
      import("@/assets/solutions-campsite.webp"),
      import("@/assets/solutions-restaurant-new.webp"),
      import("@/assets/solutions-bars.webp"),
      import("@/assets/solutions-fitness.webp"),
      import("@/assets/solutions-spa.webp"),
      import("@/assets/solutions-golf.webp"),
    ]);

    setDropdownImages({
      hotel: bgHotel.default,
      rental: bgRental.default,
      campsite: bgCampsite.default,
      restaurant: bgRestaurant.default,
      bar: bgBar.default,
      fitness: bgFitness.default,
      spa: bgSpa.default,
      golf: bgGolf.default,
    });
    setImagesLoaded(true);
  }, [imagesLoaded]);

  // Dropdown image component - shows placeholder until image loads
  const DropdownImage = ({ imageKey, className }: { imageKey: string; className?: string }) => {
    const src = dropdownImages[imageKey];
    return (
      <div className={`absolute inset-0 bg-muted ${className}`}>
        {src && (
          <img 
            src={src} 
            alt="" 
            loading="lazy" 
            decoding="async" 
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" 
          />
        )}
      </div>
    );
  };
  
  return (
    <>
    <div className="fixed top-0 left-0 right-0 z-50 px-4 pt-4">
      <nav className={`mx-auto max-w-7xl rounded-xl transition-all duration-300 ${
        scrolled 
          ? 'bg-white shadow-lg border border-gray-200' 
          : 'bg-white/95 backdrop-blur-sm shadow-md border border-gray-100'
      }`}>
        <div className="px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <img src={logo} alt="CentralFlow - AI Financial Controller for Hospitality" className="w-8 h-8" />
            <span className="font-mokoto text-lg uppercase tracking-wider text-gray-900">CentralFlow</span>
          </Link>
        
        <div className="hidden lg:flex items-center gap-1 flex-1 justify-center">
          <NavigationMenu>
            <NavigationMenuList>
              {/* Solutions Dropdown - FIRST */}
              <NavigationMenuItem>
                <NavigationMenuTrigger 
                  onPointerEnter={loadDropdownImages}
                  onFocus={loadDropdownImages}
                  className="bg-transparent h-9 px-4 text-sm font-medium transition-colors text-gray-600 hover:text-gray-900 hover:bg-gray-100 data-[state=open]:bg-gray-100 data-[state=open]:text-gray-900"
                >
                  {t('nav.solutions')}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="p-4 w-[700px] bg-white border border-gray-200 rounded-md shadow-lg">
                    <div className="mb-4">
                      <h4 className="text-xs font-bold uppercase text-primary mb-3 tracking-wider px-1">{t('solutionsNav.stayLodging')}</h4>
                      <div className="grid grid-cols-3 gap-2">
                        <Link to="/solutions/hotels" className="relative flex flex-col items-center justify-end p-3 pt-12 rounded-lg overflow-hidden group transition-all hover:ring-2 hover:ring-primary/50">
                          <DropdownImage imageKey="hotel" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10 group-hover:from-primary/80 group-hover:via-primary/40 group-hover:to-primary/10 transition-all" />
                          <div className="relative z-10 flex flex-col items-center">
                            <Hotel className="w-4 h-4 text-white mb-1" />
                            <span className="text-xs font-semibold text-white text-center">{t('solutionsNav.hotels')}</span>
                          </div>
                        </Link>
                        <Link to="/solutions/serviced-apartments-rentals" className="relative flex flex-col items-center justify-end p-3 pt-12 rounded-lg overflow-hidden group transition-all hover:ring-2 hover:ring-primary/50">
                          <DropdownImage imageKey="rental" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10 group-hover:from-primary/80 group-hover:via-primary/40 group-hover:to-primary/10 transition-all" />
                          <div className="relative z-10 flex flex-col items-center">
                            <Home className="w-4 h-4 text-white mb-1" />
                            <span className="text-xs font-semibold text-white text-center">{t('solutionsNav.servicedApartments')}</span>
                          </div>
                        </Link>
                        <Link to="/solutions/campsite" className="relative flex flex-col items-center justify-end p-3 pt-12 rounded-lg overflow-hidden group transition-all hover:ring-2 hover:ring-primary/50">
                          <DropdownImage imageKey="campsite" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10 group-hover:from-primary/80 group-hover:via-primary/40 group-hover:to-primary/10 transition-all" />
                          <div className="relative z-10 flex flex-col items-center">
                            <Tent className="w-4 h-4 text-white mb-1" />
                            <span className="text-xs font-semibold text-white text-center">{t('solutionsNav.campsite')}</span>
                          </div>
                        </Link>
                      </div>
                    </div>

                    {/* Food & Nightlife Section */}
                    <div className="mb-4">
                      <h4 className="text-xs font-bold uppercase text-primary mb-3 tracking-wider px-1">{t('solutionsNav.foodNightlife')}</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <Link to="/solutions/restaurants-cafes" className="relative flex flex-col items-center justify-end p-3 pt-12 rounded-lg overflow-hidden group transition-all hover:ring-2 hover:ring-primary/50">
                          <DropdownImage imageKey="restaurant" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10 group-hover:from-primary/80 group-hover:via-primary/40 group-hover:to-primary/10 transition-all" />
                          <div className="relative z-10 flex flex-col items-center">
                            <UtensilsCrossed className="w-4 h-4 text-white mb-1" />
                            <span className="text-xs font-semibold text-white text-center">{t('solutionsNav.restaurantsCafes')}</span>
                          </div>
                        </Link>
                        <Link to="/solutions/bars-nightlife" className="relative flex flex-col items-center justify-end p-3 pt-12 rounded-lg overflow-hidden group transition-all hover:ring-2 hover:ring-primary/50">
                          <DropdownImage imageKey="bar" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10 group-hover:from-primary/80 group-hover:via-primary/40 group-hover:to-primary/10 transition-all" />
                          <div className="relative z-10 flex flex-col items-center">
                            <Wine className="w-4 h-4 text-white mb-1" />
                            <span className="text-xs font-semibold text-white text-center">{t('solutionsNav.barsNightlife')}</span>
                          </div>
                        </Link>
                      </div>
                    </div>

                    {/* Wellness & Leisure Section */}
                    <div>
                      <h4 className="text-xs font-bold uppercase text-primary mb-3 tracking-wider px-1">{t('solutionsNav.wellnessLeisure')}</h4>
                      <div className="grid grid-cols-3 gap-2">
                        <Link to="/solutions/fitness-academies" className="relative flex flex-col items-center justify-end p-3 pt-12 rounded-lg overflow-hidden group transition-all hover:ring-2 hover:ring-primary/50">
                          <DropdownImage imageKey="fitness" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10 group-hover:from-primary/80 group-hover:via-primary/40 group-hover:to-primary/10 transition-all" />
                          <div className="relative z-10 flex flex-col items-center">
                            <Dumbbell className="w-4 h-4 text-white mb-1" />
                            <span className="text-xs font-semibold text-white text-center">{t('solutionsNav.fitnessAcademies')}</span>
                          </div>
                        </Link>
                        <Link to="/solutions/spa-wellness" className="relative flex flex-col items-center justify-end p-3 pt-12 rounded-lg overflow-hidden group transition-all hover:ring-2 hover:ring-primary/50">
                          <DropdownImage imageKey="spa" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10 group-hover:from-primary/80 group-hover:via-primary/40 group-hover:to-primary/10 transition-all" />
                          <div className="relative z-10 flex flex-col items-center">
                            <Sparkles className="w-4 h-4 text-white mb-1" />
                            <span className="text-xs font-semibold text-white text-center">{t('solutionsNav.spaWellness')}</span>
                          </div>
                        </Link>
                        <Link to="/solutions/golf" className="relative flex flex-col items-center justify-end p-3 pt-12 rounded-lg overflow-hidden group transition-all hover:ring-2 hover:ring-primary/50">
                          <DropdownImage imageKey="golf" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10 group-hover:from-primary/80 group-hover:via-primary/40 group-hover:to-primary/10 transition-all" />
                          <div className="relative z-10 flex flex-col items-center">
                            <Flag className="w-4 h-4 text-white mb-1" />
                            <span className="text-xs font-semibold text-white text-center">{t('solutionsNav.golf')}</span>
                          </div>
                        </Link>
                      </div>
                    </div>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>

              {/* Pricing */}
              <NavigationMenuItem>
                <Link to="/pricing" className={`inline-flex h-9 items-center justify-center px-4 py-2 text-sm font-medium transition-colors rounded-md ${
                  isActive('/pricing')
                    ? 'bg-primary text-primary-foreground'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}>
                  {t('nav.pricing')}
                </Link>
              </NavigationMenuItem>

              {/* Customers */}
              <NavigationMenuItem>
                <Link to="/customers" className={`inline-flex h-9 items-center justify-center px-4 py-2 text-sm font-medium transition-colors rounded-md ${
                  isActive('/customers')
                    ? 'bg-primary text-primary-foreground'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}>
                  {t('nav.customers')}
                </Link>
              </NavigationMenuItem>

              {/* Resources Dropdown - Reordered: FAQ, Guides, Blog */}
              <NavigationMenuItem>
                <NavigationMenuTrigger 
                  className={`bg-transparent h-9 px-4 text-sm font-medium transition-colors ${
                    isResourcesActive()
                      ? 'bg-primary text-primary-foreground data-[state=open]:bg-primary data-[state=open]:text-primary-foreground'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 data-[state=open]:bg-gray-100 data-[state=open]:text-gray-900'
                  }`}
                >
                  {t('nav.resources')}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="grid gap-3 p-6 w-[400px] bg-white border border-gray-200 rounded-md shadow-lg">
                    <Link to="/faq" className={`block p-3 rounded-lg transition-colors ${isActive('/faq') ? 'bg-primary/10 ring-1 ring-primary/30' : 'hover:bg-gray-50'}`}>
                      <span className={`text-sm font-medium ${isActive('/faq') ? 'text-primary' : 'text-gray-900'}`}>{t('resources.faq')}</span>
                      <p className="text-xs text-gray-500 mt-1">{t('resources.faqDesc')}</p>
                    </Link>
                    <Link to="/guides" className={`block p-3 rounded-lg transition-colors ${isActive('/guides') ? 'bg-primary/10 ring-1 ring-primary/30' : 'hover:bg-gray-50'}`}>
                      <span className={`text-sm font-medium ${isActive('/guides') ? 'text-primary' : 'text-gray-900'}`}>{t('resources.guides')}</span>
                      <p className="text-xs text-gray-500 mt-1">{t('resources.guidesDesc')}</p>
                    </Link>
                    <Link to="/blog" className={`block p-3 rounded-lg transition-colors ${isActive('/blog') ? 'bg-primary/10 ring-1 ring-primary/30' : 'hover:bg-gray-50'}`}>
                      <span className={`text-sm font-medium ${isActive('/blog') ? 'text-primary' : 'text-gray-900'}`}>{t('nav.blog')}</span>
                      <p className="text-xs text-gray-500 mt-1">{t('resources.blogDesc')}</p>
                    </Link>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>

              {/* Company Dropdown - Same style as Resources */}
              <NavigationMenuItem>
                <NavigationMenuTrigger 
                  className={`bg-transparent h-9 px-4 text-sm font-medium transition-colors ${
                    isActive('/about') || isActive('/careers')
                      ? 'bg-primary text-primary-foreground data-[state=open]:bg-primary data-[state=open]:text-primary-foreground'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 data-[state=open]:bg-gray-100 data-[state=open]:text-gray-900'
                  }`}
                >
                  {t('nav.company')}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="grid gap-3 p-6 w-[400px] bg-white border border-gray-200 rounded-md shadow-lg">
                    <Link to="/about" className={`block p-3 rounded-lg transition-colors ${isActive('/about') ? 'bg-primary/10 ring-1 ring-primary/30' : 'hover:bg-gray-50'}`}>
                      <span className={`text-sm font-medium ${isActive('/about') ? 'text-primary' : 'text-gray-900'}`}>{t('company.about')}</span>
                      <p className="text-xs text-gray-500 mt-1">Learn about our mission and team</p>
                    </Link>
                    <Link to="/careers" className={`block p-3 rounded-lg transition-colors ${isActive('/careers') ? 'bg-primary/10 ring-1 ring-primary/30' : 'hover:bg-gray-50'}`}>
                      <span className={`text-sm font-medium ${isActive('/careers') ? 'text-primary' : 'text-gray-900'}`}>{t('company.careers')}</span>
                      <p className="text-xs text-gray-500 mt-1">Join our growing team</p>
                    </Link>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="/auth" className="hidden sm:inline-flex text-sm font-medium transition-colors text-gray-900 hover:text-gray-600">
            Log In
          </Link>
          <Button size="sm" className="flex-shrink-0 hidden sm:flex rounded-lg px-5 transition-colors" asChild>
            <a href="https://calendly.com/centralflow/demo" target="_blank" rel="noopener noreferrer">
              Book a demo
            </a>
          </Button>
          
          <button 
            className="lg:hidden p-2 transition-colors text-gray-700"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        </div>
      </nav>
    </div>
      
      {/* Mobile Menu - Clean and professional (no images for performance) */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed top-20 left-4 right-4 z-40 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-5 space-y-1 max-h-[calc(100vh-6rem)] overflow-y-auto">
            {/* Stay & Lodging Section */}
            <div className="pb-3">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-primary px-3 mb-2">{t('solutionsNav.stayLodging')}</div>
              <Link to="/solutions/hotels" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors">
                <Hotel className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="text-sm font-medium text-gray-900">{t('solutionsNav.hotels')}</span>
              </Link>
              <Link to="/solutions/serviced-apartments-rentals" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors">
                <Home className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="text-sm font-medium text-gray-900">{t('solutionsNav.servicedApartments')}</span>
              </Link>
              <Link to="/solutions/campsite" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors">
                <Tent className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="text-sm font-medium text-gray-900">{t('solutionsNav.campsite')}</span>
              </Link>
            </div>
            
            {/* Food & Nightlife Section */}
            <div className="py-3 border-t border-gray-100">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-primary px-3 mb-2">{t('solutionsNav.foodNightlife')}</div>
              <Link to="/solutions/restaurants-cafes" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors">
                <UtensilsCrossed className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="text-sm font-medium text-gray-900">{t('solutionsNav.restaurantsCafes')}</span>
              </Link>
              <Link to="/solutions/bars-nightlife" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors">
                <Wine className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="text-sm font-medium text-gray-900">{t('solutionsNav.barsNightlife')}</span>
              </Link>
            </div>

            {/* Wellness & Leisure Section */}
            <div className="py-3 border-t border-gray-100">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-primary px-3 mb-2">{t('solutionsNav.wellnessLeisure')}</div>
              <Link to="/solutions/fitness-academies" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors">
                <Dumbbell className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="text-sm font-medium text-gray-900">{t('solutionsNav.fitnessAcademies')}</span>
              </Link>
              <Link to="/solutions/spa-wellness" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors">
                <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="text-sm font-medium text-gray-900">{t('solutionsNav.spaWellness')}</span>
              </Link>
              <Link to="/solutions/golf" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors">
                <Flag className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="text-sm font-medium text-gray-900">{t('solutionsNav.golf')}</span>
              </Link>
            </div>

            {/* Other Links */}
            <div className="py-3 border-t border-gray-100">
              <Link to="/pricing" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-gray-900 hover:bg-gray-50 active:bg-gray-100 rounded-lg">{t('nav.pricing')}</Link>
              <Link to="/customers" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-gray-900 hover:bg-gray-50 active:bg-gray-100 rounded-lg">{t('nav.customers')}</Link>
              <Link to="/faq" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-gray-900 hover:bg-gray-50 active:bg-gray-100 rounded-lg">{t('resources.faq')}</Link>
              <Link to="/guides" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-gray-900 hover:bg-gray-50 active:bg-gray-100 rounded-lg">{t('resources.guides')}</Link>
              <Link to="/blog" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-gray-900 hover:bg-gray-50 active:bg-gray-100 rounded-lg">{t('nav.blog')}</Link>
              <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-gray-900 hover:bg-gray-50 active:bg-gray-100 rounded-lg">{t('company.about')}</Link>
              <Link to="/careers" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-gray-900 hover:bg-gray-50 active:bg-gray-100 rounded-lg">{t('company.careers')}</Link>
            </div>

            {/* Mobile CTAs */}
            <div className="pt-3 space-y-2 border-t border-gray-100">
              <Link to="/auth" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-primary hover:bg-primary/5 rounded-lg">
                Log In
              </Link>
              <Button size="sm" className="w-full h-10 text-sm font-medium" asChild>
                <a href="https://calendly.com/centralflow/demo" target="_blank" rel="noopener noreferrer">
                  Book a demo
                </a>
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
